import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SettingsCard from '../../organisms/SettingsCard';
import FormCardGrid from '../../organisms/FormCardGrid';
import CustomFieldTable from '../../organisms/CustomFieldTable';
import Loading from '../../atoms/Loading';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import {
  deleteFormField,
  getFormFields,
  saveFormFields,
} from '../../../redux/complaint/actions';
import {
  CUSTOM_FIELD_LIBRARY,
  FIELD_ACCESS_OPTIONS,
  FIELD_TYPE_OPTIONS,
  withDefaultStatuses,
} from '../../../data/formFieldPresets';

const FIELD_TYPE_MAP = {
  CUSTOM: 'Custom',
  TEXT: 'Text',
  TEXTAREA: 'Text Area',
  SELECT: 'Select',
  DROPDOWN: 'Select',
  NUMBER: 'Number',
  DATE: 'Date',
  DATETIME: 'Date Time',
  EMAIL: 'Email',
  MULTISELECT: 'MultiSelect',
  CHECKBOX: 'Checkbox',
  RADIO: 'Radio',
  FILE: 'File',
};

const FIELD_ACCESS_MAP = {
  BOTH: 'Both',
  USER: 'User',
  ADMIN: 'Admin',
};

const FIELD_TYPE_TO_API = {
  ...Object.fromEntries(
    Object.entries(FIELD_TYPE_MAP).map(([apiValue, uiValue]) => [uiValue, apiValue]),
  ),
  Select: 'SELECT',
  Dropdown: 'SELECT',
  Textarea: 'TEXTAREA',
  'Text Area': 'TEXTAREA',
  TextField: 'TEXT',
  'Date Time': 'DATETIME',
};

const FIELD_ACCESS_TO_API = {
  ...Object.fromEntries(
    Object.entries(FIELD_ACCESS_MAP).map(([apiValue, uiValue]) => [uiValue, apiValue]),
  ),
  All: 'BOTH',
  Staff: 'USER',
  Both: 'BOTH',
  User: 'USER',
};

function toFieldKey(label) {
  const words = String(label || '')
    .trim()
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return '';
  return (
    words[0].toLowerCase() +
    words
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('')
  );
}

function isPersistedField(field){
  if(!field) return false;
  return typeof field.id === 'number' || /^\d+$/.test(String(field.id));
}

function toApiOptions(field) {
  const options = field.options || [];
  const mappings = field.parentMappings || {};
  const colors = field.optionColors || {};
  const hasParents = options.some((option) => mappings[option]);
  const hasColors = options.some((option) => colors[option]);
  if (hasParents || hasColors) {
    return {
      values: options.map((label) => {
        const value = { label };
        if (mappings[label]) value.parent = mappings[label];
        if (colors[label]) value.color = colors[label];
        return value;
      }),
    };
  }
  return { values: options };
}

function toApiFormField(field, allFields = []) {
  const fieldName = String(field.fieldLabel || '').trim();
  const labelUnchanged =
    field.originalFieldName != null &&
    fieldName === String(field.originalFieldName).trim();
  const parent = allFields.find((item) => (
    String(item.fieldLabel || '').trim() === field.dependsOn && field.dependsOn
  ));
  const fieldKey = labelUnchanged
    ? (field.fieldKey || toFieldKey(fieldName))
    : toFieldKey(fieldName);
  const parentKey = parent
    ? (parent.fieldKey || toFieldKey(parent.fieldLabel))
    : '';

  const payload = {
    fieldName,
    fieldKey,
    fieldType: FIELD_TYPE_TO_API[field.fieldType] ?? String(field.fieldType || 'CUSTOM').toUpperCase(),
    required: Boolean(field.required),
    displayOrder: Number(field.order) || 1,
    fieldAccess: FIELD_ACCESS_TO_API[field.fieldAccess] ?? (field.fieldAccess ? String(field.fieldAccess).toUpperCase() : 'BOTH'),
    hideForWorkflowStepId: field.hideForWorkflowStepId ?? null,
    options: toApiOptions(field),
    isActive: field.isActive !== false,
  };

  if (parentKey && parentKey !== fieldKey) {
    payload.dependsOnFieldKey = parentKey;
  }
  return payload;
}

function toFieldEditSnapshot(field) {
  return JSON.stringify({
    fieldLabel: String(field.fieldLabel || '').trim(),
    fieldType: field.fieldType ?? '',
    required: Boolean(field.required),
    fieldAccess: field.fieldAccess ?? '',
    hideFor: [...(field.hideFor || [])].sort(),
    dependsOn: field.dependsOn || '',
    options: field.options || [],
    parentMappings: field.parentMappings || {},
    optionColors: field.optionColors || {},
    order: Number(field.order) || 1,
  });
}

function hasFieldEdits(current, original) {
  return toFieldEditSnapshot(current) !== toFieldEditSnapshot(original);
}

function toPutPayload(fields = [], allFields = fields) {
  const savedFields = fields.filter((field) => (
    !field.isNew && String(field.fieldLabel || '').trim()
  ));
  return {
    fields: savedFields.map((field, index) => (
      toApiFormField({ ...field, order: field.order || index + 1 }, allFields)
    )),
  };
}

function persistedIdsFrom(...lists) {
  const ids = [];
  const seen = new Set();
  lists.flat().forEach((field) => {
    if (!isPersistedField(field)) return;
    const fieldId = Number(field.id);
    if (seen.has(fieldId)) return;
    seen.add(fieldId);
    ids.push(fieldId);
  });
  return ids;
}

function hideForLabel(step) {
  if (!step) return null;
  if (typeof step === 'string') return step;
  return step.name ?? step.label ?? step.title ?? null;
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function optionLabel(value) {
  if (typeof value === 'string') return value;
  return value?.label ?? '';
}

function optionColor(value) {
  if (value && typeof value === 'object' && value.color) return value.color;
  return '';
}

function normalizeFormFields(list = []) {
  const sorted = [...list].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  return withDefaultStatuses(sorted.map((item) => {
    const values = item?.options?.values ?? [];
    const parent = sorted.find((field) => field.id === item.dependsOnFieldId);

    return {
      id: item.id,
      order: item.displayOrder,
      fieldLabel: item.fieldName ?? item.fieldLabel ?? '',
      fieldType: FIELD_TYPE_MAP[item.fieldType] ?? item.fieldType ?? 'Custom',
      required: Boolean(item.required),
      fieldAccess: FIELD_ACCESS_MAP[item.fieldAccess] ?? item.fieldAccess ?? '',
      hideFor: uniqueValues([
        ...(Array.isArray(item.hideFor) ? item.hideFor : []),
        hideForLabel(item.hideForWorkflowStep),
      ]),
      dependsOn: item.dependsOnField?.fieldName ?? parent?.fieldName ?? item.dependsOn ?? '',
      options: values.map(optionLabel).filter(Boolean),
      parentMappings: Object.fromEntries(
        values
          .filter((value) => value && typeof value === 'object' && value.label)
          .map((value) => [value.label, value.parent ?? '']),
      ),
      optionColors: Object.fromEntries(
        values
          .map((value) => [optionLabel(value), optionColor(value)])
          .filter(([label, color]) => label && color),
      ),
      fieldKey: item.fieldKey,
      originalFieldName: item.fieldName ?? item.fieldLabel ?? '',
      dependsOnFieldId: item.dependsOnFieldId ?? null,
      hideForWorkflowStepId: item.hideForWorkflowStepId ?? null,
      isActive: item.isActive !== false,
      label: item.fieldName ?? item.fieldLabel ?? '',
    };
  }));
}

export default function FormFieldsStep({ stepMeta, stepperProps }) {
  const dispatch = useDispatch();
  const complaintTypeId = useSelector((store) => store.appDetails?.id );
  const { state, update } = useSettingsWizard();
  const { formFields } = state;
  const addExternalFieldRef = useRef(null);
  const [savingFieldId] = useState(null);
  const [deletingFieldId, setDeletingFieldId] = useState(null);
  const handleFieldsChange = useCallback((fields) => update({ formFields: fields }), [update]);

  const handleSaveField = useCallback(async (field) => {
    const fieldName = String(field.fieldLabel || '').trim();
    if (!fieldName) return null;
    return {
      ...field,
      fieldLabel: fieldName,
      fieldKey: toFieldKey(fieldName),
      originalFieldName: fieldName,
      isNew: false,
    };
  }, []);

  const handleDeleteField = useCallback(async (field) => {
    if(!isPersistedField(field)) return true;

    setDeletingFieldId(field.id);
    try{
      await dispatch(deleteFormField({
        complaintTypeId,
        fieldId: field.id,
      })).unwrap();
      return true;
    } finally {
      setDeletingFieldId(null);
    }
  }, [complaintTypeId, dispatch]);

  const apiFields = useSelector((store) => store.complaintAppComplaint?.formFields?.data);
  const isLoading = useSelector((store) => store.complaintAppComplaint?.formFields?.isLoading);
  const error = useSelector((store) => store.complaintAppComplaint?.formFields?.error);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dispatch(getFormFields(complaintTypeId))
      .unwrap()
      .then((payload) => {
        if (cancelled) return;
        const list = Array.isArray(payload) ? payload : payload?.data;
        const mapped = withDefaultStatuses(
          normalizeFormFields(Array.isArray(list) ? list : []),
        );
        update({ formFields: mapped });
        setHasLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setHasLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [complaintTypeId, dispatch, update]);

  const mappedFields = useMemo(
    () => withDefaultStatuses(normalizeFormFields(apiFields)),
    [apiFields],
  );
  const customLibrary = CUSTOM_FIELD_LIBRARY;
  const fieldTypeOptions = FIELD_TYPE_OPTIONS;
  const fieldAccessOptions = FIELD_ACCESS_OPTIONS;
  const hideForOptions = useMemo(
    () => uniqueValues(mappedFields.flatMap((field) => field.hideFor)),
    [mappedFields],
  );
  const gridFields = formFields.length ? formFields : mappedFields;

  const handleNext = useCallback(async () => {
    try {
      const savedFields = gridFields.filter((field) => (
        !field.isNew && String(field.fieldLabel || '').trim()
      ));
      const serverById = new Map(
        normalizeFormFields(apiFields).map((field) => [Number(field.id), field]),
      );
      const hasChanges = savedFields.some((field) => {
        if (!isPersistedField(field)) return true;
        const original = serverById.get(Number(field.id));
        return !original || hasFieldEdits(field, original);
      });

      if (!hasChanges) {
        stepperProps.onNext?.();
        return;
      }

      const orderedFields = [...savedFields].sort(
        (left, right) => Number(Boolean(left.dependsOn)) - Number(Boolean(right.dependsOn)),
      );
      const serverIds = persistedIdsFrom(apiFields, savedFields);

      for (const fieldId of serverIds) {
        try {
          await dispatch(deleteFormField({ complaintTypeId, fieldId })).unwrap();
        } catch {
          // Already removed on the server; local JSON is still the source of truth.
        }
      }
      await dispatch(saveFormFields({
        complaintTypeId,
        data: toPutPayload(orderedFields, orderedFields),
      })).unwrap();
      const refreshedPayload = await dispatch(getFormFields(complaintTypeId)).unwrap();
      const refreshedList = Array.isArray(refreshedPayload)
        ? refreshedPayload
        : refreshedPayload?.data;
      update({
        formFields: withDefaultStatuses(
          normalizeFormFields(Array.isArray(refreshedList) ? refreshedList : []),
        ),
      });
      stepperProps.onNext?.();
    } catch {
      // Stay on Form Fields; error is stored on formFields.error.
    }
  }, [apiFields, complaintTypeId, dispatch, gridFields, stepperProps, update]);

  return (
    <>
      <Stepper {...stepperProps} steps={SETTINGS_STEPS} onNext={handleNext} />
      <SettingsCard
      className="ca-settings-card--plain"
      title={stepMeta.title}
      description={stepMeta.description}
      showNext={false}
    >
      {(!hasLoaded || isLoading) ? (
        <Loading text="Loading form fields" />
      ) : error && !gridFields.length ? (
        <p className="ca-body">{error}</p>
      ) : (
      <div className="ca-form-fields">
        {error ? <p className="ca-body">{error}</p> : null}
        <CustomFieldTable
          fields={customLibrary}
          onAddField={(field) => addExternalFieldRef.current?.({ ...field })}
        />
        <FormCardGrid
          initialFields={gridFields}
          addExternalFieldRef={addExternalFieldRef}
          onChange={handleFieldsChange}
          fieldTypeOptions={fieldTypeOptions}
          fieldAccessOptions={fieldAccessOptions}
          hideForOptions={hideForOptions}
          savingFieldId={savingFieldId}
          deletingFieldId={deletingFieldId}
          onSave={handleSaveField}
          onDelete={handleDeleteField}
        />
      </div>
      )}
      </SettingsCard>
    </>
  );
}
